import { Link } from "react-router-dom";
import Reveal, { RevealItem } from "./Reveal";
import { useContentItems } from "@/hooks/useContentItems";
import { detailHref, type ContentItem } from "@/lib/insights";

function stripHtml(s: string | null | undefined): string {
  if (!s) return "";
  return s.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

function EvidenceCard({ item }: { item: ContentItem }) {
  const externalHref = item.external_url || item.legacy_source_url;
  const commonClass =
    "group surface-elevated h-full flex flex-col overflow-hidden hover:border-primary/30 transition-colors";

  const inner = (
    <>
      <div className="p-card-standard border-b border-border bg-foreground/[0.02] flex items-center gap-4 text-xs">
        <span className="uppercase tracking-eyebrow text-muted-foreground transition-colors duration-300 group-hover:text-primary">
          {stripHtml(item.evidence_type) || stripHtml(item.category) || "Evidence"}
        </span>
        {item.year && (
          <span className="font-mono text-muted-foreground">{item.year}</span>
        )}
        {item.journal && (
          <span className="font-mono text-muted-foreground truncate">{stripHtml(item.journal)}</span>
        )}
      </div>

      <div className="p-8 md:p-10 flex-1 flex flex-col">
        <h3 className="font-display text-[22px] md:text-[26px] leading-[1.2] text-foreground tracking-tight">
          {stripHtml(item.title)}
        </h3>

        <div className="mt-auto pt-8 flex items-center justify-between text-sm text-muted-foreground">
          <span className="truncate pr-4">
            {stripHtml(item.authors) || stripHtml(item.location) || ""}
          </span>
          <span
            aria-hidden
            className="transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-1 text-foreground shrink-0"
          >
            →
          </span>
        </div>
      </div>
    </>
  );

  if (externalHref) {
    return (
      <a
        href={externalHref}
        target="_blank"
        rel="noopener noreferrer"
        className={commonClass}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link to={detailHref(item)} className={commonClass}>
      {inner}
    </Link>
  );
}

function CardSkeleton() {
  return (
    <div className="surface-elevated h-full overflow-hidden">
      <div className="p-card-standard border-b border-border bg-foreground/[0.02] flex gap-4">
        <div className="h-3 w-24 bg-muted rounded animate-pulse" />
        <div className="h-3 w-12 bg-muted rounded animate-pulse" />
      </div>
      <div className="p-8 md:p-10 space-y-4">
        <div className="h-6 w-3/4 bg-muted rounded animate-pulse" />
        <div className="h-6 w-1/2 bg-muted rounded animate-pulse" />
        <div className="h-4 w-full bg-muted rounded animate-pulse" />
      </div>
    </div>
  );
}

const Evidence = () => {
  const { data, isLoading } = useContentItems({ kind: "evidence", limit: 2 });
  const items = data ?? [];

  return (
    <section className="bg-background py-section-tight border-y border-border">
      <div className="container">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="eyebrow">Evidence</p>
            <h2 className="display mt-5 text-section-title text-foreground">
              We lead with evidence.
            </h2>
          </div>
          <Link
            to="/insights/evidence"
            className="group inline-flex items-center gap-2 text-foreground font-medium border-b border-foreground/30 pb-1 hover:border-primary transition-colors duration-300 self-start md:self-auto"
          >
            See all evidence
            <span aria-hidden className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
        </Reveal>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <CardSkeleton />
            <CardSkeleton />
          </div>
        ) : items.length > 0 ? (
          <Reveal variant="stagger" stagger={0.1} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {items.map((item) => (
              <RevealItem key={item.id}>
                <EvidenceCard item={item} />
              </RevealItem>
            ))}
          </Reveal>
        ) : null}
      </div>
    </section>
  );
};

export default Evidence;
